import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/lib/toast";
import { ArrowRight, ShieldCheck, Fingerprint } from "@phosphor-icons/react";
import { formatApiErrorDetail } from "@/lib/api";
import { signInWithPasskey, passkeysSupported, friendlyPasskeyError } from "@/lib/passkeys";

export default function Login() {
  const { login, applyLogin } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [rememberDays, setRememberDays] = useState(15);
  const [busy, setBusy] = useState(false);
  const [pkBusy, setPkBusy] = useState(false);
  const [err, setErr] = useState("");
  const canPasskey = passkeysSupported();

  const goAfterLogin = (data) => {
    const internalRoles = ["administrator", "owner", "dispatcher", "freight_agent", "compliance_specialist", "accounting", "carrier_acquisition"];
    const roleDest = data.role === "promoter" ? "/promoter" : internalRoles.includes(data.role) ? "/portal" : "/dashboard";
    const from = loc.state?.from;
    nav(from && from.startsWith(roleDest) ? from : roleDest);
  };

  const passkey = async () => {
    setErr(""); setPkBusy(true);
    try {
      const data = await signInWithPasskey(email);
      applyLogin(data);
      toast.success("Welcome back");
      goAfterLogin(data);
    } catch (e) {
      const msg = friendlyPasskeyError(e);
      setErr(msg); toast.error(msg);
    } finally { setPkBusy(false); }
  };

  // Read the `?as=` hint to tailor the header (carrier vs employee).
  const audience = React.useMemo(() => {
    const params = new URLSearchParams(loc.search);
    const raw = (params.get("as") || "").toLowerCase();
    return raw === "carrier" ? "carrier" : (raw === "employee" ? "employee" : "any");
  }, [loc.search]);
  const heading = audience === "carrier"
    ? { overline: "Carrier Portal", title: "Sign in as a carrier", sub: "View your invoices, pay dispatch fees, and see loads assigned to you." }
    : audience === "employee"
      ? { overline: "Employee Portal", title: "Sign in — MFG staff", sub: "Administrators · Dispatchers · Freight Agents · Compliance · Accounting." }
      : { overline: "Employee & Member Portal", title: "Sign in to MFG", sub: "Employees and carriers — all sign in here." };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const data = await login(email, password, remember ? rememberDays : 0);
      toast.success("Welcome back");
      goAfterLogin(data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Login failed";
      setErr(msg);
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="mfg-cockpit min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src="/img/future/login-grille.jpg" alt="Semi truck grille in electric blue light" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent"></div>
        <div className="absolute inset-0 mfg-hologrid opacity-50 [mask-image:linear-gradient(to_top,black,transparent_70%)]"></div>
        <div className="mfg-orb mfg-orb-blue w-[420px] h-[420px] -bottom-24 -left-24"></div>
        <div className="mfg-radar w-[260px] h-[260px] right-10 top-24 opacity-70"></div>
        <div className="mfg-beam b2 left-0"></div>
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="mfg-mark" data-testid="login-logo">
            <span className="mfg-mark-badge"><span>MFG</span></span>
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-[15px] tracking-tight">MONTANA FREIGHT</span>
              <span className="text-[10px] tracking-[0.28em] text-white/60 font-semibold">GROUP · EST 2026</span>
            </div>
          </Link>
          <div>
            <div className="overline text-[#0A84FF] mb-4">Cockpit access</div>
            <div className="font-display font-black text-4xl leading-tight max-w-md">Fortune 500 discipline. Owner-operator hustle.</div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 mfg-tele text-[10px] tracking-[0.22em] text-white/55 uppercase">
              <span className="inline-flex items-center gap-2"><span className="mfg-hud-dot" /> Dispatch link live</span>
              <span>USDOT 6487776</span><span>MC 78839050</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mfg-hud-scene flex items-center justify-center p-6 md:p-12">
        <div className="mfg-hud-corners w-full max-w-md p-6 sm:p-8 rounded-2xl border border-[#0A84FF]/25 bg-white/5 backdrop-blur-md shadow-[0_0_60px_-20px_rgba(10,132,255,0.6)]">
          <div className="lg:hidden mb-8">
            <Link to="/" className="mfg-mark" data-mark-variant="dark">
              <span className="mfg-mark-badge"><span>MFG</span></span>
              <span className="font-display font-black text-black">MONTANA FREIGHT GROUP</span>
            </Link>
          </div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="overline text-[#0A84FF]" data-testid="login-audience-overline">{heading.overline}</div>
            <span className="mfg-tele inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] text-emerald-300 uppercase" data-testid="login-secure-chip"><span className="mfg-hud-dot" /> Secure</span>
          </div>
          <h1 className="font-display font-black text-4xl text-black mb-2">{heading.title}</h1>
          <p className="text-neutral-500 mb-8 text-sm">{heading.sub}</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="overline text-neutral-500 block mb-2">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                className="w-full bg-white border border-black/10 rounded-md px-4 py-3 text-black focus:outline-none focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF]/20" />
            </div>
            <div>
              <label className="overline text-neutral-500 block mb-2">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                className="w-full bg-white border border-black/10 rounded-md px-4 py-3 text-black focus:outline-none focus:border-[#0A84FF] focus:ring-2 focus:ring-[#0A84FF]/20" />
            </div>
            <label className="flex items-start gap-2 text-sm text-neutral-700 cursor-pointer select-none" data-testid="login-remember-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember"
                className="mt-0.5 accent-black w-4 h-4" />
              <span className="flex-1">Keep me signed in on this device for
                <span className="inline-flex gap-1 ml-2 align-middle">
                  {[15, 30].map((d) => (
                    <button key={d} type="button" data-testid={`login-remember-${d}`}
                      onClick={(e) => { e.preventDefault(); setRemember(true); setRememberDays(d); }}
                      className={`px-2 py-0.5 rounded-full text-xs font-bold border transition-colors ${remember && rememberDays === d ? "bg-black text-white border-black" : "border-black/15 text-neutral-600 hover:border-black/40"}`}>
                      {d} days
                    </button>
                  ))}
                </span>
                <span className="block text-xs text-neutral-500">Otherwise you'll stay signed in for 7 days. Don't use on shared computers.</span>
              </span>
            </label>
            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
            <button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full btn-mfg-primary px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? "Signing in..." : "Sign In"} <ArrowRight size={16} weight="bold" />
            </button>
            {canPasskey && (
              <button type="button" onClick={passkey} disabled={pkBusy || busy} data-testid="login-passkey-btn"
                className="w-full px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2 border border-neutral-300 text-black hover:bg-neutral-50 disabled:opacity-60">
                <Fingerprint size={18} weight="bold" /> {pkBusy ? "Waiting for your device…" : "Sign in with fingerprint / Face ID"}
              </button>
            )}
            <div className="pt-1 text-right">
              <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-[#0A84FF] font-semibold hover:underline">
                Forgot your password?
              </Link>
            </div>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-black/10"></div>
            <span className="text-xs text-neutral-400 font-semibold uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-black/10"></div>
          </div>
          <button type="button" data-testid="login-google-btn"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/portal";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white border border-black/15 hover:bg-neutral-50 px-6 py-3 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-3 transition-colors">
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            Continue with Google
          </button>

          <div className="mt-6 text-sm text-neutral-500 text-center">
            New to MFG? <Link to="/signup" data-testid="login-signup-link" className="text-[#0A84FF] font-semibold hover:underline">Create an account →</Link>
          </div>
          <div className="mt-3 text-sm text-neutral-500 text-center">
            Are you a carrier? <Link to="/carrier" className="text-[#0A84FF] font-semibold hover:underline">Apply to move freight →</Link>
          </div>
          <div className="mt-2 text-sm text-neutral-500 text-center">
            Approved carrier? <Link to="/activate" data-testid="login-activate-link" className="text-[#0A84FF] font-semibold hover:underline">Activate with your MC / USDOT →</Link>
          </div>
          <div className="mt-3 text-center">
            <Link to="/" className="text-xs text-neutral-500 hover:text-black">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
